import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  Search,
  TrendingDown,
  TrendingUp,
  Timeline,
  AttachMoney,
  LocalOffer,
  Schedule,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { rebateRecordsFromPricing } from '../utils/mapPricingRebates';
import { PageContainer, PageHeader, StatusChip, statusTone, statusLabel } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';
import { useLocale } from '../contexts/LocaleContext';
import { StoreSaleWindowSchedule, useStoreSaleWindowsVisible } from '../components/saleWindows/StoreSaleWindowSchedule';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

interface PriceHistoryRecord {
  id: string;
  masterPricingId: string;
  productId?: string;
  model: string;
  brand: string;
  region: 'US' | 'CA';
  changeType: 'new' | 'price_change' | 'rebate_change' | 'expired' | 'cancelled' | 'sync_rejected';
  previousValue?: {
    mapPrice?: number;
    instantRebate?: number;
    tradeCredit?: number;
    effectiveDate?: Date;
  };
  newValue?: {
    mapPrice?: number;
    instantRebate?: number;
    tradeCredit?: number;
    effectiveDate?: Date;
  };
  changedAt: Date;
  sourceSubmissionId?: string;
}

interface RebateRecord {
  id: string;
  model: string;
  brand: string;
  region: 'US' | 'CA';
  rebateAmount: number;
  rebateType: 'instant' | 'mail_in' | 'trade_credit';
  description?: string;
  effectiveDate: Date;
  endDate?: Date;
  status: 'active' | 'upcoming' | 'expired';
}

/** The most history rows one request returns; a full page means there may be more. */
const HISTORY_FETCH_LIMIT = 500;

export const PriceHistoryPage: React.FC = () => {
  const isMobile = useIsMobile();
  const { t } = useAppTranslation();
  const { formatDate: formatLocaleDate, formatCurrency: formatLocaleCurrency } = useLocale();
  // Scheduled changes from vendor-approved sale windows (MOT-370): a tab only
  // while `vendor_sale_windows` is on and a brand the store sells runs the rule.
  const showScheduled = useStoreSaleWindowsVisible();
  const [urlTab, setSelectedTab] = useUrlTab(['history', 'rebates', 'scheduled'] as const, 'history');
  const selectedTab = urlTab === 'scheduled' && !showScheduled ? 'history' : urlTab;
  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [regionFilter, setRegionFilter] = useState<string>('all');
  const [rebateStatus, setRebateStatus] = useState<string>('all');

  // Fetch price history from masterPricing
  const { data: priceHistory, isLoading: historyLoading } = useQuery<PriceHistoryRecord[]>({
    queryKey: ['price-history', brandFilter, regionFilter],
    queryFn: async () => {
      const brand = brandFilter !== 'all' ? brandFilter : undefined;
      const history = await apiService.getPricingHistory(brand, undefined, HISTORY_FETCH_LIMIT);
      
      // Transform to PriceHistoryRecord format
      return history.map((record: any) => ({
        id: record.id,
        masterPricingId: record.masterPricingId,
        model: record.model,
        brand: record.brand,
        region: record.region || 'US',
        changeType: record.changeType || 'new',
        previousValue: record.previousValue ? {
          mapPrice: record.previousValue.mapPrice,
          instantRebate: record.previousValue.instantRebate,
          tradeCredit: record.previousValue.tradeCredit,
          effectiveDate: record.previousValue.effectiveDate ? new Date(record.previousValue.effectiveDate) : undefined,
        } : undefined,
        newValue: {
          mapPrice: record.newValue?.mapPrice,
          instantRebate: record.newValue?.instantRebate,
          tradeCredit: record.newValue?.tradeCredit,
          effectiveDate: record.newValue?.effectiveDate ? new Date(record.newValue.effectiveDate) : undefined,
        },
        changedAt: record.changedAt ? new Date(record.changedAt) : new Date(),
      }));
    },
  });

  // Rebates come from the current pricing rows (the same `/map-pricing` list the
  // MAP Pricing Data page reads), not from the last 500 history events: history
  // only holds what changed recently and has no end dates, so counts drifted.
  const { data: rebates, isLoading: rebatesLoading } = useQuery<RebateRecord[]>({
    queryKey: ['price-history-rebates'],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '5000', offset: '0' });
      const response = await makeApiCall(`/map-pricing?${params}`, { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch pricing records');
      const result = await response.json();
      return rebateRecordsFromPricing(result.data || []);
    },
  });

  // Get unique brands
  const brands = useMemo(() => {
    const allRecords = [...(priceHistory || []), ...(rebates || [])];
    return Array.from(new Set(allRecords.map(r => r.brand))).sort();
  }, [priceHistory, rebates]);

  // Filter history records
  const filteredHistory = useMemo(() => {
    if (!priceHistory) return [];
    return priceHistory.filter(record => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!record.model.toLowerCase().includes(query) && 
            !record.brand.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (brandFilter !== 'all' && record.brand !== brandFilter) return false;
      if (regionFilter !== 'all' && record.region !== regionFilter) return false;
      return true;
    });
  }, [priceHistory, searchQuery, brandFilter, regionFilter]);

  // Filter rebate records
  const filteredRebates = useMemo(() => {
    if (!rebates) return [];
    return rebates.filter(record => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!record.model.toLowerCase().includes(query) && 
            !record.brand.toLowerCase().includes(query)) {
          return false;
        }
      }
      if (brandFilter !== 'all' && record.brand !== brandFilter) return false;
      if (regionFilter !== 'all' && record.region !== regionFilter) return false;
      return true;
    });
  }, [rebates, searchQuery, brandFilter, regionFilter]);

  // The summary cards count every status; only the archive table follows the status toggle.
  const tableRebates = useMemo(
    () => (rebateStatus === 'all' ? filteredRebates : filteredRebates.filter((r) => r.status === rebateStatus)),
    [filteredRebates, rebateStatus]
  );

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return formatLocaleCurrency(value);
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '-';
    return formatLocaleDate(date);
  };

  // `changedAt` is when the change was recorded; the price may take effect on a
  // different (often later) date. Show it apart, and flag a future one as upcoming.
  const effectiveNote = (record: PriceHistoryRecord): { date: Date; upcoming: boolean } | null => {
    const effective = record.newValue?.effectiveDate;
    if (!effective || Number.isNaN(effective.getTime())) return null;
    if (Math.abs(effective.getTime() - record.changedAt.getTime()) < 24 * 60 * 60 * 1000) return null;
    return { date: effective, upcoming: effective.getTime() > Date.now() };
  };
  const renderEffective = (record: PriceHistoryRecord) => {
    const note = effectiveNote(record);
    if (!note) return null;
    return (
      <Typography variant="caption" color="text.secondary" display="block" noWrap title={t('storePricingScope.effectiveUpcomingHint')}>
        {note.upcoming ? t('storePricingScope.upcoming') : t('storePricingScope.effective')} {formatDate(note.date)}
      </Typography>
    );
  };

  // A 'new' record has no prior price: its Previous column shows a dash and no
  // up/down arrow, even if the stored previous value mirrors the new one.
  const hasPriorMap = (record: PriceHistoryRecord): boolean =>
    record.changeType !== 'new' && !!record.previousValue?.mapPrice && !!record.newValue?.mapPrice;
  const previousMap = (record: PriceHistoryRecord): string =>
    record.changeType === 'new' ? '\u2014' : formatCurrency(record.previousValue?.mapPrice);

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'new':
        return <LocalOffer color="success" fontSize="small" />;
      case 'price_change':
        return <AttachMoney color="primary" fontSize="small" />;
      case 'rebate_change':
        return <TrendingDown color="info" fontSize="small" />;
      case 'expired':
        return <Schedule color="disabled" fontSize="small" />;
      case 'cancelled':
        return <Schedule color="error" fontSize="small" />;
      default:
        return <Timeline fontSize="small" />;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return <StatusChip label="Active" tone="positive" size="small" />;
      case 'upcoming':
        return <StatusChip label="Upcoming" tone="info" size="small" />;
      case 'expired':
        return <StatusChip label="Expired" tone="neutral" size="small" />;
      default:
        return <StatusChip label={statusLabel(status)} tone={statusTone(status)} size="small" />;
    }
  };

  return (
    <PageContainer>
      <PageHeader title="Price History" description="Track pricing changes and view rebate history" />

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Timeline color="primary" />
                <Box>
                  <Typography variant="h5">
                    {(priceHistory?.length || 0) >= HISTORY_FETCH_LIMIT ? `${HISTORY_FETCH_LIMIT}+` : priceHistory?.length || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Price Changes</Typography>
                  {(priceHistory?.length || 0) >= HISTORY_FETCH_LIMIT && (
                    <Typography variant="caption" color="text.secondary">
                      {t('storePricingScope.changesCapped', { count: HISTORY_FETCH_LIMIT })}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TrendingDown color="success" />
                <Box>
                  <Typography variant="h5">
                    {filteredRebates.filter(r => r.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Rebates</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Schedule color="info" />
                <Box>
                  <Typography variant="h5">
                    {filteredRebates.filter(r => r.status === 'upcoming').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Upcoming Rebates</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AttachMoney color="disabled" />
                <Box>
                  <Typography variant="h5">
                    {filteredRebates.filter(r => r.status === 'expired').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Expired Rebates</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={selectedTab} onChange={(_, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
        <Tab label="Price History" icon={<Timeline />} iconPosition="start" value="history" />
        <Tab label="Rebate Archive" icon={<TrendingDown />} iconPosition="start" value="rebates" />
        {showScheduled && <Tab label={t('store.saleWindows.schedule.tab')} icon={<Schedule />} iconPosition="start" value="scheduled" />}
      </Tabs>

      {selectedTab === 'scheduled' && <StoreSaleWindowSchedule />}

      {/* Filters */}
      {selectedTab !== 'scheduled' && (
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              placeholder="Search by model or brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                label="Brand"
              >
                <MenuItem value="all">All Brands</MenuItem>
                {brands.map(brand => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Region</InputLabel>
              <Select
                value={regionFilter}
                onChange={(e) => setRegionFilter(e.target.value)}
                label="Region"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="US">US</MenuItem>
                <MenuItem value="CA">Canada</MenuItem>
              </Select>
            </FormControl>

            {selectedTab === 'rebates' && (
              <ToggleButtonGroup
                value={rebateStatus}
                exclusive
                onChange={(_, v) => v && setRebateStatus(v)}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="upcoming">Upcoming</ToggleButton>
                <ToggleButton value="expired">Expired</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Stack>
        </CardContent>
      </Card>
      )}

      {/* Price History Tab */}
      {selectedTab === 'history' && (
        <Card>
          {isMobile ? (
            historyLoading ? (
              <Stack sx={{ p: 2 }} spacing={1.5}>
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={72} />)}
              </Stack>
            ) : filteredHistory.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No price history found</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {filteredHistory.map((record) => (
                  <Box key={record.id} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{record.model}</Typography>
                        <Typography variant="body2" color="text.secondary">{record.brand}</Typography>
                      </Box>
                      <Chip label={record.region} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                      {getChangeIcon(record.changeType)}
                      <Chip
                        label={String(record.changeType || 'change').replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Previous MAP</Typography>
                        <Typography variant="body2" color="text.secondary">{previousMap(record)}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>New MAP</Typography>
                        <Typography variant="body2" fontWeight="medium">{formatCurrency(record.newValue?.mapPrice)}</Typography>
                        {hasPriorMap(record) && record.previousValue?.mapPrice && record.newValue?.mapPrice && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {record.newValue.mapPrice < record.previousValue.mapPrice ? (
                              <TrendingDown color="success" sx={{ fontSize: 14 }} />
                            ) : (
                              <TrendingUp color="error" sx={{ fontSize: 14 }} />
                            )}
                            <Typography variant="caption" color={record.newValue.mapPrice < record.previousValue.mapPrice ? 'success.main' : 'error.main'}>
                              {Math.abs(((record.newValue.mapPrice - record.previousValue.mapPrice) / record.previousValue.mapPrice) * 100).toFixed(1)}%
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>{t('storePricingScope.recorded')}</Typography>
                        <Typography variant="body2">{formatDate(record.changedAt)}</Typography>
                        {renderEffective(record)}
                      </Box>
                      {(record.previousValue?.instantRebate !== record.newValue?.instantRebate) && (
                        <Box>
                          <Typography component="span" sx={mobileLabelSx}>Rebate Change</Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(record.previousValue?.instantRebate)}
                            </Typography>
                            <Typography variant="caption">→</Typography>
                            <Typography variant="caption" color="success.main" fontWeight="medium">
                              {formatCurrency(record.newValue?.instantRebate)}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            )
          ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('storePricingScope.recorded')}</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Previous MAP</TableCell>
                  <TableCell align="right">New MAP</TableCell>
                  <TableCell align="right">Rebate Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No price history found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHistory.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>{formatDate(record.changedAt)}</Typography>
                        {renderEffective(record)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {getChangeIcon(record.changeType)}
                          <Chip
                            label={String(record.changeType || 'change').replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{record.model}</Typography>
                      </TableCell>
                      <TableCell>{record.brand}</TableCell>
                      <TableCell>
                        <Chip label={record.region} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {previousMap(record)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(record.newValue?.mapPrice)}
                        </Typography>
                        {hasPriorMap(record) && record.previousValue?.mapPrice && record.newValue?.mapPrice && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {record.newValue.mapPrice < record.previousValue.mapPrice ? (
                              <TrendingDown color="success" sx={{ fontSize: 14 }} />
                            ) : (
                              <TrendingUp color="error" sx={{ fontSize: 14 }} />
                            )}
                            <Typography variant="caption" color={record.newValue.mapPrice < record.previousValue.mapPrice ? 'success.main' : 'error.main'}>
                              {Math.abs(((record.newValue.mapPrice - record.previousValue.mapPrice) / record.previousValue.mapPrice) * 100).toFixed(1)}%
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(record.previousValue?.instantRebate !== record.newValue?.instantRebate) && (
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(record.previousValue?.instantRebate)}
                            </Typography>
                            <Typography variant="caption">→</Typography>
                            <Typography variant="caption" color="success.main" fontWeight="medium">
                              {formatCurrency(record.newValue?.instantRebate)}
                            </Typography>
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Card>
      )}

      {/* Rebate Archive Tab */}
      {selectedTab === 'rebates' && (
        <Card>
          {isMobile ? (
            rebatesLoading ? (
              <Stack sx={{ p: 2 }} spacing={1.5}>
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} height={72} />)}
              </Stack>
            ) : tableRebates.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">No rebates found</Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />}>
                {tableRebates.map((rebate) => (
                  <Box key={rebate.id} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2">{rebate.model}</Typography>
                        <Typography variant="body2" color="text.secondary">{rebate.brand}</Typography>
                      </Box>
                      {getStatusChip(rebate.status)}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                      <Chip label={rebate.region} size="small" variant="outlined" />
                      <Chip
                        label={rebate.rebateType === 'instant' ? 'Instant' : 'Trade Credit'}
                        size="small"
                        color={rebate.rebateType === 'instant' ? 'success' : 'info'}
                        variant="outlined"
                      />
                    </Stack>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Amount</Typography>
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          -{formatCurrency(rebate.rebateAmount)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Effective</Typography>
                        <Typography variant="body2">{formatDate(rebate.effectiveDate)}</Typography>
                      </Box>
                      <Box>
                        <Typography component="span" sx={mobileLabelSx}>Expires</Typography>
                        <Typography variant="body2">{rebate.endDate ? formatDate(rebate.endDate) : '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )
          ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Effective</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rebatesLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : tableRebates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No rebates found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  tableRebates.map((rebate) => (
                    <TableRow key={rebate.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{rebate.model}</Typography>
                      </TableCell>
                      <TableCell>{rebate.brand}</TableCell>
                      <TableCell>
                        <Chip label={rebate.region} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={rebate.rebateType === 'instant' ? 'Instant' : 'Trade Credit'}
                          size="small"
                          color={rebate.rebateType === 'instant' ? 'success' : 'info'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium" color="success.main">
                          -{formatCurrency(rebate.rebateAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(rebate.effectiveDate)}</TableCell>
                      <TableCell>
                        {rebate.endDate ? formatDate(rebate.endDate) : '-'}
                      </TableCell>
                      <TableCell>{getStatusChip(rebate.status)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Card>
      )}
    </PageContainer>
  );
};

export default PriceHistoryPage;

